import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const resources = {
  fr: {
    translation: {
      nav: {
        home: 'Accueil',
        about: 'À propos',
        activities: 'Activités',
        products: 'Produits',
        impact: 'Impact',
        formation: 'Formations',
        gallery: 'Galerie',
        news: 'Actualités',
        visit: 'Visiter',
        partners: 'Partenaires',
        support: 'Nous soutenir',
        support_partner: 'Devenir partenaire',
        sponsorship: 'Parrainer',
        experiences: 'Visites & Expériences',
        shop: 'Boutique',
        contact: 'Contact',
        farm: 'La ferme',
        activities_training: 'Activités & Formations',
        stories: 'Actualités',
      },
      common: {
        learn_more: 'En savoir plus',
        discover: 'Découvrir',
        contact_us: 'Nous contacter',
        read_more: 'Lire la suite',
        all: 'Tout',
        loading: 'Chargement...',
        send: 'Envoyer',
        back: 'Retour',
        close: 'Fermer',
        cancel: 'Annuler',
        view_all: 'Voir tout',
      },
      home: {
        hero_title: 'Cultiver la terre,\nrestaurer la vie.',
        hero_sub: "Centre de Permaculture Lawatan — pionnier de l'agroécologie et de la résilience climatique au Burkina Faso depuis 2006.",
        cta_discover: 'Découvrir le centre',
        cta_activities: 'Nos activités',
        intro_overline: 'Notre engagement',
        intro_title: "Une agriculture qui régénère, des communautés qui prospèrent.",
        intro_text: "Depuis près de vingt ans, le centre Lawatan développe une agriculture qui prend soin du vivant. Nous cultivons, transformons, formons et inspirons — en plaçant la terre, l'eau et les femmes au cœur de chaque action.",
        featured_overline: "À la une · Distinction 2026",
        featured_read: "Lire l'histoire complète",
        domains_overline: "Nos domaines d'action",
        domains_title: "Six piliers, une vision intégrée.",
        impact_overline: 'Impact 2025',
        impact_title: 'Des chiffres qui racontent une trajectoire.',
        stats: {
          years: 'Années d\'expérience',
          tons: 'Tonnes produites en 2025',
          certifications: 'Certifications biologiques',
          beneficiaries: 'Coopératives & stagiaires accompagnés',
        },
        products_overline: 'Nos produits',
        products_title: 'Le meilleur de la nature, certifié bio.',
        formations_overline: 'Apprendre, transmettre',
        formations_title: 'Un centre vivant de formation et de recherche.',
        testimonial_overline: 'Témoignage',
        cta_visit_title: 'Venez visiter la ferme.',
        cta_visit_sub: "Une immersion au cœur d'un écosystème productif et inspirant.",
      },
      about: {
        title: 'Notre histoire',
        overline: 'Depuis 2006',
        intro: "Le Centre de Permaculture Lawatan est né d'une conviction simple : nourrir l'Afrique en régénérant ses sols et ses savoirs. Sous l'impulsion de Madame Marthe TOE/PARE, l'aventure démarre en 2006 comme une ferme agroforestière. Aujourd'hui, c'est une entreprise (SARL) reconnue, à la croisée de la production, de la formation et de la recherche.",
        founder_title: 'Une vision incarnée',
        founder_name: 'Madame Marthe TOE/PARE',
        founder_role: 'Fondatrice & Directrice',
        founder_bio: "Pionnière de la permaculture au Burkina Faso, Marthe TOE/PARE a consacré sa vie à promouvoir une agriculture nourricière, équitable et résiliente. Sa vision : réconcilier santé humaine, vitalité des écosystèmes et économie locale.",
        mission_title: 'Mission',
        mission_text: "Développer des systèmes de production durables et innovants, transformer et valoriser les Produits Forestiers Non Ligneux (moringa, baobab) et accompagner les producteurs par la formation et le conseil.",
        vision_title: 'Vision',
        vision_text: "Concilier les trois dimensions essentielles d'une agriculture durable : santé humaine et environnementale, résilience climatique et économie circulaire et inclusive.",
        values_title: 'Nos valeurs',
        values: [
          { t: 'Durabilité', d: "Chaque geste pense les générations futures." },
          { t: 'Biodiversité', d: 'Cultiver la diversité du vivant comme une richesse.' },
          { t: 'Autonomie', d: 'Former, transmettre, libérer les capacités locales.' },
          { t: 'Excellence', d: "Une exigence de qualité dans chaque produit, chaque formation." },
        ],
      },
      activities: {
        title: 'Nos activités',
        overline: 'Production · Transformation · Formation · Recherche',
        intro: "Six domaines complémentaires forment le cœur d'une démarche intégrée, où chaque activité nourrit les autres.",
        items: [
          { t: 'Production agricole', d: "Cultures de moringa, de baobab, maraîchage et plantes médicinales en agroécologie." },
          { t: 'Transformation agroalimentaire', d: 'Poudres, huiles, tisanes et cosmétiques élaborés sur place dans le respect du bio.' },
          { t: 'Formation & sensibilisation', d: "Programmes pour producteurs, coopératives, étudiants et stagiaires internationaux." },
          { t: 'Recherche & innovation', d: "Études sur la fertilité des sols, la biodiversité et la résilience climatique." },
          { t: 'Appui aux coopératives', d: "Renforcement des coopératives féminines : techniques, qualité, marchés." },
          { t: 'Conservation de la biodiversité', d: 'Préservation des espèces locales et restauration des écosystèmes.' },
        ],
      },
      products: {
        title: 'Nos produits',
        overline: 'Moringa · Baobab · Bio certifié',
        intro: "Des produits issus d'une agriculture exigeante, transformés sur place, certifiés ECOCERT et BioSPG.",
        certifications_title: 'Nos certifications',
        cert_ecocert: 'Certification internationale bio.',
        cert_biospg: 'Programme national bio (CNABio).',
        benefits_title: 'Bienfaits',
      },
      impact: {
        title: 'Notre impact',
        overline: 'Environnemental · Social · Économique',
        intro: "Mesurer ce qui compte vraiment : la santé des sols, l'autonomie des femmes, la résilience des territoires.",
        env_title: 'Impact environnemental',
        env_text: "Restauration des sols, agroforesterie, gestion de l'eau, valorisation de l'énergie solaire et conservation de la biodiversité.",
        social_title: 'Impact social',
        social_text: "Formation des coopératives féminines, accompagnement des stagiaires, transmission intergénérationnelle des savoirs paysans.",
        eco_title: 'Impact économique',
        eco_text: "Création de revenus locaux, structuration des filières moringa et baobab, accès aux marchés régionaux et internationaux.",
      },
      formation: {
        title: 'Formations & Recherche',
        overline: 'Apprendre, transmettre, innover',
        intro: "Le centre Lawatan est un lieu vivant d'apprentissage, ouvert aux producteurs, aux coopératives et aux stagiaires du monde entier.",
        topics_label: 'Thématiques',
        duration_label: 'Durée',
        research_title: 'Recherche & innovation',
        research_text: "Nos recherches portent notamment sur la production biologique d'oignon avec biofertilisants et l'inventaire de la biodiversité végétale locale.",
      },
      gallery: {
        title: 'Galerie',
        overline: 'Plongez dans notre quotidien',
        intro: 'Images du centre, de la ferme, des formations et des moments forts.',
        all: 'Tout',
        farm: 'Ferme',
        products: 'Produits',
        events: 'Événements',
      },
      news: {
        title: 'Actualités',
        overline: 'Vie du centre',
        intro: 'Articles, événements, projets en cours et avancées de la recherche.',
        all: 'Tout',
        actualite: 'Actualités',
        evenement: 'Événements',
        projet: 'Projets',
      },
      visit: {
        title: 'Visiter la ferme',
        overline: 'Une expérience immersive',
        intro: "Plongez au cœur de notre écosystème : visites guidées, ateliers pratiques et séjours d'immersion sont possibles toute l'année.",
        info_title: 'Informations pratiques',
        location: 'Localisation',
        location_v: 'Village de Dodougou, à 35 km au sud de Bobo-Dioulasso.',
        when: 'Période',
        when_v: 'Toute l\'année, sur rendez-vous.',
        languages: 'Langues',
        languages_v: 'Français, anglais.',
        cta: 'Organiser ma visite',
      },
      partners: {
        title: 'Nos partenaires',
        overline: 'Travailler ensemble',
        intro: 'Nous collaborons avec des organismes de certification, des réseaux nationaux et des coopératives locales pour démultiplier notre impact.',
      },
      experiences: {
        overline: 'Visites · Ateliers · Immersions · Séminaires',
        title: 'Vivre la permaculture\nen Afrique de l\'Ouest.',
        sub: "Visites guidées, ateliers pratiques, immersions de plusieurs jours, séminaires sur-mesure pour entreprises et ONG : choisissez le format qui vous correspond.",
        cta_explore: 'Découvrir nos formats',
        principles_overline: 'Notre promesse',
        principles_title: 'Une expérience utile, sincère et bien organisée.',
        principles: [
          'Encadrement par des permaculteurs et formateurs expérimentés.',
          'Repas partagés issus de notre ferme bio.',
          'Programme adapté à votre groupe et à vos objectifs.',
          'Tarifs transparents, devis personnalisé sur demande.',
          'Hébergement possible sur place pour les séjours.',
        ],
        offerings_overline: 'Formats disponibles',
        offerings_title: 'Choisissez votre expérience.',
        categories: { all: 'Tout', visit: 'Visite', workshop: 'Atelier', immersion: 'Immersion', corporate: 'Entreprise / ONG' },
        details: 'Détails',
        book: 'Réserver',
        includes: 'Inclus',
        audience: 'Public visé',
        custom_overline: 'Sur-mesure',
        custom_title: "Un projet hors format ?",
        custom_text: "Vous préparez un voyage d'études, un séminaire d'équipe, un workshop d'école ou un tournage ? Décrivez-nous votre besoin et nous construirons un programme dédié.",
        custom_cta: 'Demander un devis sur-mesure',
        f_name: 'Nom complet',
        f_email: 'Email',
        f_phone: 'Téléphone',
        f_country: 'Pays',
        f_organization: 'Organisation (facultatif)',
        f_group_size: 'Taille du groupe',
        f_dates: 'Dates souhaitées',
        f_message: 'Message',
        submit: 'Envoyer la demande',
        sent_title: 'Demande envoyée !',
        sent_sub: "Nous revenons vers vous sous 3 jours ouvrés avec une proposition.",
        error: 'Une erreur est survenue. Merci de réessayer.',
      },
      shop: {
        overline: 'Boutique en ligne · Bio certifié',
        title: 'Goûtez le meilleur de la ferme.',
        sub: "Moringa et baobab, cultivés et transformés sur notre ferme au Burkina Faso, certifiés ECOCERT et BioSPG. Livraison France et Europe.",
        farm_to_jar: 'De la ferme au pot',
        add: 'Ajouter au panier',
        cart: 'Mon panier',
        cart_empty: 'Votre panier est vide pour l\'instant.',
        back_to_shop: 'Retour à la boutique',
        total: 'Total',
        email_label: 'Email',
        email_ph: 'votre@email.com',
        checkout: 'Passer au paiement',
        secure_payment: 'Paiement sécurisé · Stripe',
        empty_catalog: 'Aucun produit disponible pour le moment.',
        shipping_overline: 'Livraison',
        shipping_title: 'Expédié depuis Bobo-Dioulasso.',
        shipping_text: 'Nous expédions en Europe via partenaire logistique. Délais : 7 à 14 jours selon destination. Pour des commandes en gros (coopératives, distributeurs), contactez-nous directement.',
        success_title: 'Commande confirmée — merci !',
        success_sub: 'Vous recevrez un email de confirmation dans les minutes qui suivent.',
        fail_title: 'Paiement non finalisé',
        fail_sub: "Aucun montant n'a été prélevé. Vous pouvez réessayer à tout moment.",
        timeout_title: 'Vérification en cours',
        timeout_sub: "Le statut tarde à arriver. Pas d'inquiétude, vous serez notifié par email dès la confirmation.",
        checking_title: 'Vérification du paiement…',
        checking_sub: 'Merci de patienter, ne fermez pas cette page.',
        checkout_error: 'Impossible de démarrer le paiement. Réessayez.',
      },
      support: {
        overline: 'Bailleurs · ONG · Recherche · Médias',
        title: 'Investir dans une\nagriculture qui régénère.',
        sub: "Vous êtes une fondation, une organisation internationale, un partenaire technique ou financier ? Rejoignez un projet à fort impact, mesurable et ancré localement depuis 2006.",
        cta_apply: 'Devenir partenaire',
        cta_dossier: 'Télécharger le dossier institutionnel',
        why_overline: 'Pourquoi Lawatan',
        why_title: 'Un partenariat lisible, un impact mesurable.',
        why_items: [
          { t: '19+ années de terrain', d: "Depuis 2006, une expertise éprouvée en agroécologie, transformation et formation." },
          { t: 'Certifications reconnues', d: "ECOCERT et BioSPG : un cadre qualité aligné sur les standards internationaux." },
          { t: 'Impact triple', d: "Environnemental, social et économique — chaque action est traçable." },
          { t: 'Gouvernance solide', d: "Manuel de procédures, comptabilité structurée, transparence opérationnelle." },
          { t: 'Communautés au cœur', d: "Coopératives féminines, stagiaires, producteurs : un écosystème vivant." },
          { t: 'Échelle régionale', d: "Membre du CNABio, présence sur les filières moringa et baobab du Burkina Faso." },
        ],
        types_overline: 'Modes de partenariat',
        types_title: 'Plusieurs façons de construire ensemble.',
        types: [
          { t: 'Soutien financier', d: "Subventions, mécénat, financement de projets ciblés (sols, eau, transformation, formation)." },
          { t: 'Partenariat technique', d: "Apport d'expertise, équipements, transfert de technologies, échanges sud-sud / nord-sud." },
          { t: 'Recherche & innovation', d: "Recherche action, accueil de doctorants, publications conjointes." },
          { t: 'ONG & coopérations', d: "Programmes communs sur la résilience climatique, la sécurité alimentaire, l'autonomisation des femmes." },
          { t: 'Médias & plaidoyer', d: "Reportages, documentaires, campagnes de visibilité." },
          { t: 'Autre / sur-mesure', d: "Une autre idée ? Nous étudions toutes les propositions à fort impact." },
        ],
        form_overline: 'Candidature',
        form_title: 'Parlons de votre projet de partenariat.',
        form_sub: "Le formulaire ci-dessous nous permettra de qualifier rapidement votre demande et de vous répondre sous 7 jours ouvrés.",
        f_organization: 'Organisation',
        f_contact_name: 'Nom du contact',
        f_role: 'Fonction',
        f_email: 'Email professionnel',
        f_phone: 'Téléphone',
        f_country: 'Pays',
        f_website: 'Site web',
        f_partner_type: 'Type de partenariat',
        f_focus_areas: "Domaines d'intérêt",
        f_budget: 'Enveloppe envisagée (EUR)',
        f_timeline: 'Horizon de mise en œuvre',
        f_message: 'Présentation du projet',
        f_message_ph: "Décrivez le projet, vos objectifs, vos critères d'impact et toute info utile.",
        partner_types: {
          funding: 'Soutien financier',
          technical: 'Partenariat technique',
          research: 'Recherche',
          ngo: 'ONG / coopération',
          media: 'Médias / plaidoyer',
          other: 'Autre',
        },
        focus: {
          agroecology: 'Agroécologie',
          training: 'Formation',
          biodiversity: 'Biodiversité',
          women: 'Coopératives féminines',
          processing: 'Transformation',
          climate: 'Résilience climatique',
        },
        budgets: {
          'lt50': '< 50 000 €',
          '50-150': '50 000 – 150 000 €',
          '150-500': '150 000 – 500 000 €',
          'gt500': '> 500 000 €',
          'inkind': 'Apport en nature',
        },
        timelines: { 'short': '< 3 mois', 'mid': '3–6 mois', 'long': '6–12 mois', 'xlong': '> 12 mois' },
        submit: 'Envoyer ma candidature',
        sent_title: 'Merci pour votre candidature !',
        sent_sub: 'Notre équipe revient vers vous sous 7 jours ouvrés.',
        error: 'Une erreur est survenue. Merci de réessayer ou de nous écrire à lawatanperma@gmail.com.',
        impact_title: 'Notre engagement vis-à-vis des partenaires.',
        impact_items: [
          'Reporting transparent (financier, opérationnel, impact).',
          'Indicateurs co-construits, mesurables et publiés.',
          'Restitution annuelle et visites terrain organisées.',
          'Co-communication respectueuse des marques et des engagements.',
        ],
      },
      sponsorship: {
        overline: 'Mécénat & Parrainage',
        title: 'Adoptez un baobab. Restaurez un hectare. Changez la donne.',
        intro: "Votre soutien finance directement la plantation, l'irrigation, la formation et la recherche. Chaque contribution est tracée, documentée et restituée.",
        popular: 'Le plus choisi',
        choose: 'Choisir cette formule',
        your_contribution: 'Votre contribution',
        sponsor_name: 'Votre nom (optionnel)',
        sponsor_email: 'Votre email (pour le reçu)',
        sponsor_message: 'Un message à l\'équipe Lawatan ? (optionnel)',
        proceed_payment: 'Continuer vers le paiement',
        loading: 'Chargement...',
        error_generic: 'Une erreur est survenue. Merci de réessayer.',
        success_title: 'Merci infiniment !',
        success_sub: 'Votre soutien fait une vraie différence. Un email de confirmation et votre certificat de parrainage vous seront envoyés sous 48h.',
        back_home: "Retour à l'accueil",
        view_plans: 'Voir les autres formules',
        fail_title: 'Paiement non finalisé',
        fail_sub: 'Le paiement n\'a pas abouti. Aucun montant n\'a été prélevé.',
        timeout_title: 'Vérification en cours',
        timeout_sub: 'Le paiement met un peu plus de temps. Un email vous sera envoyé dès confirmation.',
        checking_title: 'Vérification du paiement...',
        checking_sub: 'Merci de patienter quelques secondes.',
        back_to_plans: 'Retour aux formules',
      },
      contact: {
        title: 'Contact',
        overline: 'Parlons ensemble',
        intro: "Vous êtes partenaire, étudiant, journaliste ou simple curieux : écrivez-nous.",
        name: 'Nom',
        email: 'Email',
        subject: 'Sujet',
        message: 'Message',
        send: 'Envoyer le message',
        sent: 'Merci, votre message a bien été envoyé.',
        error: 'Une erreur est survenue. Merci de réessayer.',
        info_title: 'Coordonnées',
        address: 'Adresse',
        address_v: '01 BP 15 Bobo-Dioulasso 01, Burkina Faso',
        site: 'Site',
        site_v: 'Village de Dodougou, 35 km sud de Bobo-Dioulasso',
        phone: 'Téléphone',
        email_label: 'Email',
      },
      footer: {
        tagline: 'Cultiver la terre, restaurer la vie.',
        rights: 'Tous droits réservés.',
        nav_title: 'Navigation',
        contact_title: 'Contact',
        social_title: 'Suivez-nous',
        legal: 'Mentions légales',
      },
      admin: {
        login_title: 'Espace administrateur',
        login_sub: 'Connectez-vous pour gérer les contenus.',
        email: 'Email',
        password: 'Mot de passe',
        login: 'Se connecter',
        logout: 'Déconnexion',
        dashboard: 'Tableau de bord',
        articles: 'Actualités',
        products: 'Produits',
        formations: 'Formations',
        partners: 'Partenaires',
        gallery: 'Galerie',
        projects: 'Projets',
        messages: 'Messages',
        experiences: 'Expériences',
        bookings: 'Réservations',
        partnerships: 'Candidatures partenariat',
        stats: 'Statistiques',
        sponsorships: 'Parrainages',
        add: 'Ajouter',
        edit: 'Modifier',
        delete: 'Supprimer',
        save: 'Enregistrer',
        cancel: 'Annuler',
      },
    },
  },
  en: {
    translation: {
      nav: {
        home: 'Home', about: 'About', activities: 'Activities', products: 'Products', impact: 'Impact',
        formation: 'Trainings', gallery: 'Gallery', news: 'News', visit: 'Visit', partners: 'Partners',
        support: 'Support us', support_partner: 'Become a partner', sponsorship: 'Sponsor a tree',
        experiences: 'Visits & Experiences', shop: 'Shop', contact: 'Contact',
        farm: 'The farm', activities_training: 'Activities & Trainings', stories: 'Stories',
      },
      common: {
        learn_more: 'Learn more', discover: 'Discover', contact_us: 'Contact us', read_more: 'Read more',
        all: 'All', loading: 'Loading...', send: 'Send', back: 'Back', close: 'Close', cancel: 'Cancel', view_all: 'View all',
      },
      home: {
        hero_title: 'Cultivate the land,\nrestore life.',
        hero_sub: 'Centre de Permaculture Lawatan — pioneer of agroecology and climate resilience in Burkina Faso since 2006.',
        cta_discover: 'Discover the center',
        cta_activities: 'Our activities',
        intro_overline: 'Our commitment',
        intro_title: 'Agriculture that regenerates, communities that thrive.',
        intro_text: "For nearly twenty years, the Lawatan center has been growing an agriculture that cares for the living. We cultivate, transform, train and inspire — placing the land, water and women at the heart of every action.",
        featured_overline: "Featured · 2026 Distinction",
        featured_read: "Read the full story",
        domains_overline: 'Our fields of action',
        domains_title: 'Six pillars, one integrated vision.',
        impact_overline: 'Impact 2025',
        impact_title: 'Numbers that tell a journey.',
        stats: {
          years: 'Years of experience',
          tons: 'Tons produced in 2025',
          certifications: 'Organic certifications',
          beneficiaries: 'Cooperatives & interns supported',
        },
        products_overline: 'Our products',
        products_title: 'The best of nature, certified organic.',
        formations_overline: 'Learn, share',
        formations_title: 'A living center for training and research.',
        testimonial_overline: 'Testimony',
        cta_visit_title: 'Come visit the farm.',
        cta_visit_sub: 'An immersion at the heart of a productive and inspiring ecosystem.',
      },
      about: {
        title: 'Our story', overline: 'Since 2006',
        intro: "The Centre de Permaculture Lawatan was born from a simple conviction: feed Africa by regenerating its soils and its knowledge. Driven by Mrs. Marthe TOE/PARE, the journey began in 2006 as an agroforestry farm. Today, it is a recognized company (SARL), at the crossroads of production, training and research.",
        founder_title: 'A vision embodied',
        founder_name: 'Mrs. Marthe TOE/PARE',
        founder_role: 'Founder & Director',
        founder_bio: "A pioneer of permaculture in Burkina Faso, Marthe TOE/PARE has dedicated her life to nourishing, fair and resilient agriculture. Her vision: reconciling human health, ecosystem vitality and local economy.",
        mission_title: 'Mission',
        mission_text: "Develop sustainable and innovative production systems, transform and add value to Non-Timber Forest Products (moringa, baobab), and support producers through training and advice.",
        vision_title: 'Vision',
        vision_text: 'Reconcile the three essential dimensions of sustainable agriculture: human and environmental health, climate resilience, and a circular and inclusive economy.',
        values_title: 'Our values',
        values: [
          { t: 'Sustainability', d: 'Every act considers future generations.' },
          { t: 'Biodiversity', d: 'Cultivating diversity of life as a wealth.' },
          { t: 'Autonomy', d: 'Train, share, free local capacities.' },
          { t: 'Excellence', d: 'A demand for quality in every product and training.' },
        ],
      },
      activities: {
        title: 'Our activities',
        overline: 'Production · Processing · Training · Research',
        intro: 'Six complementary fields form the heart of an integrated approach, where each activity nourishes the others.',
        items: [
          { t: 'Agricultural production', d: 'Moringa, baobab, market gardening and medicinal plants grown using agroecology.' },
          { t: 'Agri-food processing', d: 'Powders, oils, herbal teas and cosmetics processed on site according to organic standards.' },
          { t: 'Training & awareness', d: 'Programs for producers, cooperatives, students and international interns.' },
          { t: 'Research & innovation', d: 'Studies on soil fertility, biodiversity and climate resilience.' },
          { t: 'Cooperative support', d: 'Strengthening women\'s cooperatives: techniques, quality, markets.' },
          { t: 'Biodiversity conservation', d: 'Preservation of local species and ecosystem restoration.' },
        ],
      },
      products: {
        title: 'Our products', overline: 'Moringa · Baobab · Certified organic',
        intro: 'Products from a demanding agriculture, processed on site, certified ECOCERT and BioSPG.',
        certifications_title: 'Our certifications',
        cert_ecocert: 'International organic certification.',
        cert_biospg: 'National organic program (CNABio).',
        benefits_title: 'Benefits',
      },
      impact: {
        title: 'Our impact', overline: 'Environmental · Social · Economic',
        intro: "Measuring what truly matters: soil health, women's autonomy, the resilience of territories.",
        env_title: 'Environmental impact',
        env_text: 'Soil restoration, agroforestry, water management, solar energy valorization, and biodiversity conservation.',
        social_title: 'Social impact',
        social_text: "Training of women's cooperatives, intern support, intergenerational transmission of farming knowledge.",
        eco_title: 'Economic impact',
        eco_text: 'Creation of local incomes, structuring of moringa and baobab value chains, access to regional and international markets.',
      },
      formation: {
        title: 'Trainings & Research', overline: 'Learn, transmit, innovate',
        intro: 'The Lawatan center is a living place of learning, open to producers, cooperatives and interns from around the world.',
        topics_label: 'Topics', duration_label: 'Duration',
        research_title: 'Research & innovation',
        research_text: 'Our research focuses on organic onion production with biofertilizers and inventory of local plant biodiversity.',
      },
      gallery: { title: 'Gallery', overline: 'Step into our daily life', intro: 'Images of the center, the farm, trainings and key moments.', all: 'All', farm: 'Farm', products: 'Products', events: 'Events' },
      news: { title: 'News', overline: 'Center life', intro: 'Articles, events, ongoing projects and research advances.', all: 'All', actualite: 'News', evenement: 'Events', projet: 'Projects' },
      visit: {
        title: 'Visit the farm', overline: 'An immersive experience',
        intro: 'Step into the heart of our ecosystem: guided tours, hands-on workshops and immersion stays are possible all year long.',
        info_title: 'Practical information',
        location: 'Location', location_v: 'Village of Dodougou, 35 km south of Bobo-Dioulasso.',
        when: 'When', when_v: 'All year, by appointment.',
        languages: 'Languages', languages_v: 'French, English.',
        cta: 'Plan my visit',
      },
      partners: { title: 'Our partners', overline: 'Working together', intro: 'We collaborate with certifying bodies, national networks and local cooperatives to scale our impact.' },
      experiences: {
        overline: 'Tours · Workshops · Immersions · Seminars',
        title: 'Live permaculture\nin West Africa.',
        sub: 'Guided tours, hands-on workshops, multi-day immersions, custom seminars for companies and NGOs: choose the format that fits you.',
        cta_explore: 'Explore our formats',
        principles_overline: 'Our promise',
        principles_title: 'A useful, sincere and well-organized experience.',
        principles: [
          'Coaching by experienced permaculturists and trainers.',
          'Shared meals from our organic farm.',
          'Program tailored to your group and goals.',
          'Transparent pricing, custom quote on request.',
          'On-site accommodation available for stays.',
        ],
        offerings_overline: 'Available formats',
        offerings_title: 'Choose your experience.',
        categories: { all: 'All', visit: 'Tour', workshop: 'Workshop', immersion: 'Immersion', corporate: 'Corporate / NGO' },
        details: 'Details', book: 'Book',
        includes: 'Included', audience: 'Audience',
        custom_overline: 'Custom',
        custom_title: 'A project off the menu?',
        custom_text: 'Planning a study trip, a team seminar, a school workshop or a film shoot? Tell us your need and we will design a dedicated program.',
        custom_cta: 'Request a custom quote',
        f_name: 'Full name', f_email: 'Email', f_phone: 'Phone', f_country: 'Country',
        f_organization: 'Organization (optional)',
        f_group_size: 'Group size', f_dates: 'Preferred dates', f_message: 'Message',
        submit: 'Send request',
        sent_title: 'Request sent!', sent_sub: 'We will come back to you within 3 business days with a proposal.',
        error: 'An error occurred. Please try again.',
      },
      shop: {
        overline: 'Online shop · Certified organic',
        title: 'Taste the best of the farm.',
        sub: 'Moringa and baobab, grown and processed on our farm in Burkina Faso, certified ECOCERT and BioSPG. Shipping across Europe.',
        farm_to_jar: 'From farm to jar',
        add: 'Add to cart',
        cart: 'My cart',
        cart_empty: 'Your cart is empty for now.',
        back_to_shop: 'Back to shop',
        total: 'Total',
        email_label: 'Email',
        email_ph: 'your@email.com',
        checkout: 'Proceed to payment',
        secure_payment: 'Secure payment · Stripe',
        empty_catalog: 'No products available right now.',
        shipping_overline: 'Shipping',
        shipping_title: 'Shipped from Bobo-Dioulasso.',
        shipping_text: 'We ship across Europe via logistics partner. Delivery: 7-14 days depending on destination. For bulk orders (cooperatives, distributors), contact us directly.',
        success_title: 'Order confirmed — thank you!',
        success_sub: 'You will receive a confirmation email within the next few minutes.',
        fail_title: 'Payment not completed',
        fail_sub: 'No amount was charged. You can retry at any time.',
        timeout_title: 'Verification in progress',
        timeout_sub: "Status is taking a moment. No worries, you'll be notified by email once confirmed.",
        checking_title: 'Verifying payment…',
        checking_sub: 'Please wait, do not close this page.',
        checkout_error: 'Could not start payment. Please retry.',
      },
      support: {
        overline: 'Funders · NGOs · Research · Media',
        title: 'Invest in agriculture\nthat regenerates.',
        sub: "Are you a foundation, an international organization, a technical or financial partner? Join a high-impact project, measurable and locally rooted since 2006.",
        cta_apply: 'Become a partner',
        cta_dossier: 'Download the institutional dossier',
        why_overline: 'Why Lawatan',
        why_title: 'A clear partnership, a measurable impact.',
        why_items: [
          { t: '19+ years on the ground', d: 'Since 2006, proven expertise in agroecology, processing and training.' },
          { t: 'Recognized certifications', d: 'ECOCERT and BioSPG: a quality framework aligned with international standards.' },
          { t: 'Triple impact', d: 'Environmental, social and economic — every action is traceable.' },
          { t: 'Strong governance', d: 'Procedures manual, structured accounting, operational transparency.' },
          { t: 'Communities at the heart', d: "Women's cooperatives, interns, producers: a living ecosystem." },
          { t: 'Regional scale', d: 'CNABio member, active in moringa and baobab value chains in Burkina Faso.' },
        ],
        types_overline: 'Partnership modes',
        types_title: 'Many ways to build together.',
        types: [
          { t: 'Financial support', d: 'Grants, sponsorship, funding of targeted projects (soils, water, processing, training).' },
          { t: 'Technical partnership', d: 'Expertise, equipment, technology transfer, south-south / north-south exchanges.' },
          { t: 'Research & innovation', d: 'Action research, hosting PhD students, joint publications.' },
          { t: 'NGO & cooperation', d: "Joint programs on climate resilience, food security, women's empowerment." },
          { t: 'Media & advocacy', d: 'Reports, documentaries, visibility campaigns.' },
          { t: 'Other / custom', d: 'Another idea? We consider all high-impact proposals.' },
        ],
        form_overline: 'Application',
        form_title: "Let's talk about your partnership project.",
        form_sub: 'The form below helps us qualify your request quickly and respond within 7 business days.',
        f_organization: 'Organization', f_contact_name: 'Contact name', f_role: 'Role',
        f_email: 'Professional email', f_phone: 'Phone', f_country: 'Country',
        f_website: 'Website', f_partner_type: 'Partnership type',
        f_focus_areas: 'Areas of interest', f_budget: 'Estimated budget (EUR)',
        f_timeline: 'Implementation horizon', f_message: 'Project description',
        f_message_ph: 'Describe the project, your goals, your impact criteria and any relevant info.',
        partner_types: { funding: 'Financial support', technical: 'Technical partnership', research: 'Research', ngo: 'NGO / cooperation', media: 'Media / advocacy', other: 'Other' },
        focus: { agroecology: 'Agroecology', training: 'Training', biodiversity: 'Biodiversity', women: "Women's cooperatives", processing: 'Processing', climate: 'Climate resilience' },
        budgets: { 'lt50': '< €50,000', '50-150': '€50,000 – 150,000', '150-500': '€150,000 – 500,000', 'gt500': '> €500,000', 'inkind': 'In-kind contribution' },
        timelines: { 'short': '< 3 months', 'mid': '3–6 months', 'long': '6–12 months', 'xlong': '> 12 months' },
        submit: 'Send my application',
        sent_title: 'Thank you for your application!',
        sent_sub: 'Our team will get back to you within 7 business days.',
        error: 'An error occurred. Please try again or write to lawatanperma@gmail.com.',
        impact_title: 'Our commitment to partners.',
        impact_items: [
          'Transparent reporting (financial, operational, impact).',
          'Co-built, measurable and published indicators.',
          'Annual review and on-site visits arranged.',
          'Respectful co-communication of brands and commitments.',
        ],
      },
      sponsorship: {
        overline: 'Patronage & Sponsorship',
        title: 'Adopt a baobab. Restore a hectare. Make a difference.',
        intro: 'Your support directly funds planting, irrigation, training and research. Every contribution is tracked, documented and reported back.',
        popular: 'Most popular',
        choose: 'Choose this plan',
        your_contribution: 'Your contribution',
        sponsor_name: 'Your name (optional)',
        sponsor_email: 'Your email (for the receipt)',
        sponsor_message: 'A message to the Lawatan team? (optional)',
        proceed_payment: 'Continue to payment',
        loading: 'Loading...',
        error_generic: 'An error occurred. Please try again.',
        success_title: 'Thank you so much!',
        success_sub: 'Your support truly matters. A confirmation email and your sponsorship certificate will be sent within 48 hours.',
        back_home: 'Back to home',
        view_plans: 'View other plans',
        fail_title: 'Payment not completed',
        fail_sub: 'The payment did not succeed. No amount was charged.',
        timeout_title: 'Verification in progress',
        timeout_sub: 'The payment is taking a bit longer. An email will be sent to you upon confirmation.',
        checking_title: 'Verifying payment...',
        checking_sub: 'Please wait a few seconds.',
        back_to_plans: 'Back to plans',
      },
      contact: {
        title: 'Contact', overline: "Let's talk",
        intro: 'Whether you are a partner, student, journalist or curious mind: write to us.',
        name: 'Name', email: 'Email', subject: 'Subject', message: 'Message',
        send: 'Send message', sent: 'Thank you, your message has been sent.', error: 'An error occurred. Please try again.',
        info_title: 'Reach us',
        address: 'Address', address_v: '01 BP 15 Bobo-Dioulasso 01, Burkina Faso',
        site: 'Site', site_v: 'Village of Dodougou, 35 km south of Bobo-Dioulasso',
        phone: 'Phone', email_label: 'Email',
      },
      footer: { tagline: 'Cultivate the land, restore life.', rights: 'All rights reserved.', nav_title: 'Navigation', contact_title: 'Contact', social_title: 'Follow us', legal: 'Legal' },
      admin: {
        login_title: 'Admin area', login_sub: 'Sign in to manage content.',
        email: 'Email', password: 'Password', login: 'Sign in', logout: 'Logout',
        dashboard: 'Dashboard', articles: 'News', products: 'Products', formations: 'Trainings',
        partners: 'Partners', gallery: 'Gallery', projects: 'Projects', messages: 'Messages',
        experiences: 'Experiences', bookings: 'Bookings', partnerships: 'Partnership applications',
        stats: 'Statistics', sponsorships: 'Sponsorships',
        add: 'Add', edit: 'Edit', delete: 'Delete', save: 'Save', cancel: 'Cancel',
      },
    },
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    lng: typeof window !== 'undefined' ? localStorage.getItem('i18nextLng') || 'fr' : 'fr',
    fallbackLng: 'fr',
    supportedLngs: ['fr', 'en'],
    interpolation: { escapeValue: false },
    detection: { order: ['localStorage'], caches: ['localStorage'] },
  });

export default i18n;
